/* ==========================================================================
   Oliviera Home — product card colour swatches (v2)
   --------------------------------------------------------------------------
   - Desktop (mouse): hovering a swatch previews that colour's photo;
     leaving the swatch row returns to the chosen colour. Click to choose.
   - Touch: the photo is preloaded the moment a finger lands on the swatch,
     then swapped on tap — no blank flash.
   - Chosen swatch gets a visible ring; the colour name shows under the row.
   - Price, sold-out badge, card links and Quick view follow the chosen colour.
   - Event delegation, so it keeps working after filtering / pagination.

   Data comes from snippets/pcard-color-swatch.liquid (data-* on each swatch)
   and snippets/card-product.liquid (data-card-image / -secondary).
   ========================================================================== */
(() => {
  if (window.__olvSwatchesV2) return;
  window.__olvSwatchesV2 = true;

  const SWATCH = '[data-variant-swatch]';
  const CARD = '[data-product-card]';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const chosenByProduct = new Map(); // product url path -> colour value (for Quick view)
  const preloaded = new Map(); // url -> Promise

  /* ---------- image helpers ---------- */

  const WIDTHS = [165, 360, 533, 720, 940, 1066, 1200, 1500];

  function cleanUrl(url) {
    if (!url) return '';
    const u = url.startsWith('//') ? location.protocol + url : url;
    try {
      const parsed = new URL(u, location.origin);
      parsed.searchParams.delete('width');
      parsed.searchParams.delete('height');
      return parsed.toString();
    } catch (e) {
      return url.split('?')[0];
    }
  }

  function sized(url, w) {
    const u = new URL(url);
    u.searchParams.set('width', w);
    return u.toString();
  }

  function srcsetFor(url) {
    return WIDTHS.map((w) => `${sized(url, w)} ${w}w`).join(', ');
  }

  function preload(url, sizes) {
    if (!url) return Promise.resolve();
    const base = cleanUrl(url);
    const key = base + '|' + sizes;
    if (preloaded.has(key)) return preloaded.get(key);
    const img = new Image();
    img.sizes = sizes || '450px';
    img.srcset = srcsetFor(base);
    img.src = sized(base, 720);
    const p = (img.decode ? img.decode() : Promise.resolve()).catch(() => {});
    preloaded.set(key, p);
    return p;
  }

  function setImage(el, url) {
    if (!el || !url) return Promise.resolve();
    const base = cleanUrl(url);
    if (el.dataset.olvCurrent === base) return Promise.resolve();
    const sizes = el.getAttribute('sizes') || '450px';
    el.dataset.olvCurrent = base;
    return preload(base, sizes).then(() => {
      if (el.dataset.olvCurrent !== base) return; // a newer pick won
      el.srcset = srcsetFor(base);
      el.src = sized(base, 720);
    });
  }

  /* ---------- card state ---------- */

  function rememberOriginal(card) {
    if (card.__olvOriginal) return;
    const main = card.querySelector('[data-card-image]');
    const second = card.querySelector('[data-card-image-secondary]');
    const label = card.querySelector('[data-card-color-name]');
    const price = card.querySelector('.product-card__info .f-price');
    card.__olvOriginal = {
      main: main ? cleanUrl(main.currentSrc || main.src) : '',
      second: second ? cleanUrl(second.currentSrc || second.src) : '',
      label: label ? label.textContent : '',
      priceHTML: price ? price.innerHTML : '',
      priceClass: price ? price.className : '',
    };
  }

  function showPhotos(card, swatch) {
    const main = card.querySelector('[data-card-image]');
    const second = card.querySelector('[data-card-image-secondary]');
    if (swatch) {
      setImage(main, swatch.dataset.image);
      if (second && swatch.dataset.imageSecondary) setImage(second, swatch.dataset.imageSecondary);
    } else if (card.__olvOriginal) {
      setImage(main, card.__olvOriginal.main);
      if (second && card.__olvOriginal.second) setImage(second, card.__olvOriginal.second);
    }
  }

  /* ---------- "second image on hover" coordination ----------
     The theme fades in the product's 2nd photo whenever the mouse is over the
     card, which covered the colour swap. We use the theme's own
     .disable-hover-image switch:
       - while the mouse is on the swatch row, the swapped photo stays visible;
       - after choosing a colour that has no photo of its own for hover, hover
         stays off for that card, so it never flashes a different colour. */

  function samePhoto(a, b) {
    if (!a || !b) return false;
    try {
      return new URL(cleanUrl(a)).pathname === new URL(cleanUrl(b)).pathname;
    } catch (e) {
      return false;
    }
  }

  function syncHover(card) {
    const swatch = card.__olvChosen;
    const orig = card.__olvOriginal || {};
    let noHover = false;
    if (swatch && !samePhoto(swatch.dataset.image, orig.main)) {
      const own = swatch.dataset.imageSecondary;
      noHover = !own || samePhoto(own, orig.second);
    }
    const off = noHover || !!card.__olvOnSwatches;
    card.classList.toggle('disable-hover-image', off);
    const main = card.querySelector('.product-card__image--main');
    if (main) main.style.transitionDuration = card.__olvOnSwatches ? '0.2s' : '';
  }

  function showName(card, swatch) {
    const label = card.querySelector('[data-card-color-name]');
    if (!label) return;
    label.textContent = swatch ? swatch.dataset.color || swatch.title || '' : '';
  }

  function showPrice(card, swatch) {
    const price = card.querySelector('.product-card__info .f-price');
    if (!price || !swatch || !swatch.dataset.price) return;
    const regular = price.querySelector('.f-price__regular .f-price-item--regular');
    const sale = price.querySelector('.f-price__sale .f-price-item--sale');
    const was = price.querySelector('.f-price__sale s');
    const onSale = !!swatch.dataset.compare;
    price.classList.toggle('f-price--on-sale', onSale);
    price.classList.toggle('f-price--sold-out', swatch.dataset.available === 'false');
    if (onSale) {
      if (regular) regular.innerHTML = '';
      if (sale) sale.innerHTML = swatch.dataset.price;
      if (was) was.innerHTML = swatch.dataset.compare;
    } else {
      if (regular) regular.innerHTML = swatch.dataset.price;
      if (sale) sale.innerHTML = '';
      if (was) was.innerHTML = '';
    }
  }

  function showSoldOut(card, swatch) {
    const list = card.querySelector('[data-card-swatches]');
    if (!list || list.dataset.showSoldout === 'false' || !swatch) return;
    const wrapper = card.querySelector('.product-card__image-wrapper');
    if (!wrapper) return;
    // Hide the theme's product-level badge; show ours for the chosen colour.
    // (inline style, because the theme's .f-badge display rule overrides [hidden])
    wrapper
      .querySelectorAll('.f-badge--soldout:not([data-olv-soldout])')
      .forEach((b) => b.style.setProperty('display', 'none', 'important'));
    let box = wrapper.querySelector('.product-card__badge');
    if (!box) {
      box = document.createElement('div');
      box.className = 'product-card__badge';
      wrapper.appendChild(box);
    }
    let badge = box.querySelector('[data-olv-soldout]');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'f-badge f-badge--soldout';
      badge.setAttribute('data-olv-soldout', '');
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = list.dataset.soldoutLabel || 'Sold out';
      box.prepend(badge);
    }
    if (swatch.dataset.available === 'false') badge.style.removeProperty('display');
    else badge.style.setProperty('display', 'none', 'important');
  }

  function syncLinks(card, swatch) {
    if (!swatch.dataset.variantId) return;
    card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      if (a.closest(SWATCH)) return;
      const u = new URL(a.getAttribute('href'), location.origin);
      u.searchParams.set('variant', swatch.dataset.variantId);
      a.setAttribute('href', u.pathname + u.search);
    });
    const productLink = card.querySelector('.product-card__title a, a[href*="/products/"]');
    if (productLink) {
      const path = new URL(productLink.getAttribute('href'), location.origin).pathname;
      chosenByProduct.set(path, swatch.dataset.color || swatch.title);
    }
  }

  function choose(swatch) {
    const card = swatch.closest(CARD);
    if (!card) return;
    rememberOriginal(card);
    card.querySelectorAll(SWATCH).forEach((el) => {
      const on = el === swatch;
      el.classList.toggle('is-active', on);
      const a = el.querySelector('a');
      if (a) a.setAttribute('aria-current', on ? 'true' : 'false');
    });
    card.__olvChosen = swatch;
    showPhotos(card, swatch);
    showName(card, swatch);
    showPrice(card, swatch);
    showSoldOut(card, swatch);
    syncLinks(card, swatch);
    syncHover(card);
  }

  /* ---------- desktop hover preview ---------- */

  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType !== 'mouse' || !canHover.matches) return;
      const list = e.target.closest && e.target.closest('[data-card-swatches]');
      const listCard = list && list.closest(CARD);
      if (listCard && !listCard.__olvOnSwatches) {
        rememberOriginal(listCard);
        listCard.__olvOnSwatches = true;
        syncHover(listCard);
      }
      const swatch = e.target.closest && e.target.closest(SWATCH);
      if (!swatch) return;
      const card = swatch.closest(CARD);
      if (!card) return;
      rememberOriginal(card);
      showPhotos(card, swatch);
      showName(card, swatch);
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (e) => {
      if (e.pointerType !== 'mouse' || !canHover.matches) return;
      const list = e.target.closest && e.target.closest('[data-card-swatches]');
      if (!list || (e.relatedTarget && list.contains(e.relatedTarget))) return;
      const card = list.closest(CARD);
      if (!card) return;
      card.__olvOnSwatches = false;
      syncHover(card);
      const chosen = card.__olvChosen;
      showPhotos(card, chosen || null);
      showName(card, chosen || null);
    },
    true
  );

  /* ---------- touch: preload as soon as the finger lands ---------- */

  document.addEventListener(
    'pointerdown',
    (e) => {
      const swatch = e.target.closest && e.target.closest(SWATCH);
      if (!swatch) return;
      const card = swatch.closest(CARD);
      const main = card && card.querySelector('[data-card-image]');
      preload(swatch.dataset.image, main ? main.getAttribute('sizes') : '450px');
    },
    { capture: true, passive: true }
  );

  /* ---------- choose: click / tap / keyboard ---------- */

  document.addEventListener(
    'click',
    (e) => {
      const swatch = e.target.closest && e.target.closest(SWATCH);
      if (!swatch || !swatch.closest(CARD)) return;
      e.preventDefault();
      e.stopPropagation();
      choose(swatch);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const swatch = e.target.closest && e.target.closest(SWATCH);
      if (!swatch || !swatch.closest(CARD)) return;
      e.preventDefault();
      e.stopPropagation();
      choose(swatch);
    },
    true
  );

  /* ---------- Quick view opens on the chosen colour ---------- */

  document.addEventListener('quick-view:loaded', (e) => {
    const url = e.detail && e.detail.productUrl;
    if (!url) return;
    const path = new URL(url, location.origin).pathname;
    const colour = chosenByProduct.get(path);
    if (!colour) return;
    const modal = [...document.querySelectorAll('quick-view-modal')].find(
      (m) => m.dataset.productUrl && new URL(m.dataset.productUrl, location.origin).pathname === path
    );
    const scope = modal || document;
    const input = [...scope.querySelectorAll('input[type="radio"]')].find((i) => i.value === colour);
    if (input && !input.checked) {
      input.click();
      return;
    }
    const select = [...scope.querySelectorAll('select')].find((s) =>
      [...s.options].some((o) => o.value === colour)
    );
    if (select && select.value !== colour) {
      select.value = colour;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
})();